import { Component, inject, Input } from '@angular/core';
import { FormControl, FormGroupDirective, NgForm } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { deepEqual, pairs } from '@tytapp/common';
import { COMPARISON_OPERATORS, ComparisonOperatorDefinition, ConditionVariable, ContentBlocksService, SimpleCondition } from '@tytapp/content/content-blocks.service';

class ValidationMatcher implements ErrorStateMatcher {
    constructor(readonly condition: () => boolean) {
    }

    isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
        return this.condition();
    }
}

@Component({
    selector: 'tyt-simple-condition-editor',
    templateUrl: './simple-condition-editor.component.html',
    styleUrl: './simple-condition-editor.component.scss',
    standalone: false
})
export class SimpleConditionEditorComponent {
    readonly variables: ConditionVariable[] = inject(ContentBlocksService).getConditionVariables();
    readonly snackBar = inject(MatSnackBar);

    readonly comparisonOperators = pairs(COMPARISON_OPERATORS);
    readonly regexValidator = new ValidationMatcher(() => this.isInvalidRegex());

    @Input() condition: SimpleCondition;

    indices(array: any[]) {
        if (!Array.isArray(array))
            return [];

        return array.map((_, i) => i);
    }

    comparisonOperatorAllowed(operator: ComparisonOperatorDefinition) {
        if (operator.disallowedForTypes) {
            if (operator.disallowedForTypes.includes(this.variable.type))
                return false;
        }

        if (operator.onlyForTypes) {
            if (!operator.onlyForTypes.includes(this.variable.type))
                return false;
        }

        return true;
    }

    removeOperand(index: number) {
        if (!Array.isArray(this.condition.operand))
            return;

        this.condition.operand.splice(index, 1);
    }

    private _knownCondition: string;

    ngDoCheck() {
        const change = (desc: string, callback: () => {}) => {
            //this.snackBar.open(`${desc}`, undefined, { duration: 3000 });
            setTimeout(callback);
        }

        if (!this.variable)
            return;

        if (this._knownCondition !== JSON.stringify(this.condition)) {
            this._knownCondition = JSON.stringify(this.condition);

            if (this.condition.operator === 'in') {
                if (!Array.isArray(this.condition.operand)) {
                    change('item -> array', () => this.condition.operand = [ this.condition.operand ]);
                }
            } else {
                if (Array.isArray(this.condition.operand)) {
                    change('array -> item', () => this.condition.operand = this.condition.operand[0]);
                }
            }

            if (this.variable.type === 'boolean' && this.condition.operator !== 'equal') {
                change('boolean cannot use in operator', () => this.condition.operator = 'equal');
            }

            if (this.variable.type !== 'string' && ['prefix', 'suffix'].includes(this.condition.operator)) {
                change('prefix/suffix operators are only available for string', () => this.condition.operator = 'equal');
            }

            let coercedValue = this.coerce(this.condition.operand);
            if (!deepEqual(coercedValue, this.condition.operand)) {
                console.log(`COERCED, was: `, this.condition.operand, `now: `, coercedValue)
                change(`coerced`, () => this.condition.operand = coercedValue);
            }
        }
    }

    coerce(value: any) {
        if (!this.variable)
            return value;

        if (Array.isArray(value))
            return value.map(x => this.coerce(x));

        if (this.variable.type === 'boolean')
            return Boolean(value);
        if (this.variable.type === 'string')
            return String(value);

        if (this.variable.type === 'enum') {
            if (!this.variable.enumOptions?.some(x => x.value === value)) {
                this.snackBar.open(`Value '${this.condition.operand}' is not a valid value for ${this.variable.name}`, undefined, { duration: 3000, panelClass: 'error' });
                return this.variable.enumOptions?.[0]?.value;
            }
        }

        return value;
    }

    get variable() {
        return this.variables.find(x => x.name === this.condition.variable);
    }

    isInvalidRegex() {
        try {
            new RegExp(this.condition.operand)
            return false;
        } catch (e) {
            return true;
        }
    }
}